import { InputError } from '@backstage/errors';
import { UserSearchItem } from '../../models/UserSearchItem';
import { DataResult } from '../../utils/DataResult';
import {
  SearchUsersOptions,
  UpsertUserOptions,
  UserAuthorizationPayload,
  UserProjectRefPayload,
  UserRefPayload,
} from '../api';
import { BaseApiService, toRequestError } from './http';

function isUserRef(value: unknown): value is UserRefPayload {
  const userRef = value as UserRefPayload | null;

  return (
    typeof userRef === 'object' &&
    userRef !== null &&
    typeof userRef.userId === 'string' &&
    userRef.userId.length > 0 &&
    typeof userRef.displayName === 'string'
  );
}

function isUserAuthorization(
  value: unknown,
): value is UserAuthorizationPayload {
  const authorization = value as { user?: unknown } | null;

  return (
    typeof authorization === 'object' &&
    authorization !== null &&
    typeof authorization.user === 'object' &&
    authorization.user !== null
  );
}

function isUserProjectRef(value: unknown): value is UserProjectRefPayload {
  const projectRef = value as UserProjectRefPayload | null;

  return (
    typeof projectRef === 'object' &&
    projectRef !== null &&
    typeof projectRef.projectKey === 'string' &&
    projectRef.projectKey.length > 0 &&
    typeof projectRef.displayName === 'string'
  );
}

function isUserSearchItem(value: unknown): value is UserSearchItem {
  const item = value as UserSearchItem | null;

  return (
    typeof item === 'object' &&
    item !== null &&
    typeof item.id === 'string' &&
    item.id.length > 0
  );
}

/**
 * Handles users: lookups, authorization, email verification and
 * provisioning confirmation links sent to users.
 */
export class UserService extends BaseApiService {
  async getUserRef(searchTerm: string): Promise<UserRefPayload | undefined> {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/user/ref/${encodeURIComponent(searchTerm)}`,
    );

    if (response.status === 404) {
      return undefined;
    }

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<unknown> | null = await response.json();

    return isUserRef(body?.data) ? body.data : undefined;
  }

  async getUser(userId: string): Promise<UserAuthorizationPayload | undefined> {
    if (!userId) {
      throw new InputError('A user id is required to load a user');
    }

    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/user/${encodeURIComponent(userId)}`,
    );

    if (response.status === 404) {
      return undefined;
    }

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<unknown> | null = await response.json();

    return isUserAuthorization(body?.data) ? body.data : undefined;
  }

  async upsertUser({
    user,
    signal,
  }: UpsertUserOptions): Promise<UserAuthorizationPayload | undefined> {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(`${baseUrl}/user/`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(user),
      signal,
    });

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<unknown> | null = await response.json();

    return isUserAuthorization(body?.data) ? body.data : undefined;
  }

  async getUserProjectRef(
    projectKey: string,
  ): Promise<UserProjectRefPayload | undefined> {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/user/refs/project/${encodeURIComponent(projectKey)}`,
    );

    if (response.status === 404) {
      return undefined;
    }

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: DataResult<unknown> | null = await response.json();

    return isUserProjectRef(body?.data) ? body.data : undefined;
  }

  async searchUsers({
    searchTerm,
    queryParams,
    signal,
  }: SearchUsersOptions): Promise<UserSearchItem[]> {
    return this.fetchArray(
      `/user/search/${encodeURIComponent(searchTerm)}${
        queryParams ? `?${queryParams}` : ''
      }`,
      isUserSearchItem,
      { signal },
    );
  }

  async confirmProvisioning(path: string, requestId: string): Promise<void> {
    if (!path.startsWith('/')) {
      throw new InputError(
        `Confirmation path '${path}' has to be relative to the service root`,
      );
    }

    const baseUrl = await this.getBaseUrl();
    const query = new URLSearchParams({ requestId });

    await this.post(`${baseUrl}${path}?${query.toString()}`);
  }

  async verifyEmail(verificationCode: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const query = new URLSearchParams({ verificationCode });

    await this.post(
      `${baseUrl}/emailverification/verifyEmail?${query.toString()}`,
    );
  }
}
